import React, { useState, useEffect } from 'react';
import { Modal } from '../..';
import { FaPhoneSlash, FaMinus, FaExpand, FaPhoneAlt } from 'react-icons/fa';
import { Duration } from 'luxon';
import { WhatsAppCurrentCall } from '../../../models/WhatsAppCall';

interface WhatsAppCallModalProps {
  call: WhatsAppCurrentCall;
  connectionState: RTCIceConnectionState;
  onTerminate: () => void;
  isTerminating?: boolean;
}

export const WhatsAppCallModal: React.FC<WhatsAppCallModalProps> = ({
  call,
  connectionState,
  onTerminate,
  isTerminating = false
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (call.status === 'connected') {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - call.startedAt.getTime()) / 1000);
        setDuration(elapsed);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [call.status, call.startedAt]);

  const getStatusDisplay = () => {
    switch (call.status) {
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return Duration.fromObject({ seconds: duration }).toFormat("mm:ss");
      case 'failed':
        return 'Call Failed';
      default:
        return 'Unknown Status';
    }
  };

  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected':
      case 'completed':
        return 'has-text-success';
      case 'checking':
        return 'has-text-warning';
      case 'failed':
      case 'disconnected':
        return 'has-text-danger';
      case 'new':
      case 'closed':
        return 'has-text-grey';
      default:
        return 'has-text-grey';
    }
  };

  if (isMinimized) {
    return (
      <div
        className="card is-floating"
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          width: "320px",
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}
      >
        <div className="card-content p-3">
          <div className="is-flex is-justify-content-space-between is-align-items-center">
            <div className="is-flex is-align-items-center">
              <div
                className="is-flex is-justify-content-center is-align-items-center has-background-success-light mr-3"
                style={{ width: "40px", height: "40px", borderRadius: "50%" }}
              >
                <FaPhoneAlt className="has-text-success" size={20} />
              </div>
              <div>
                <p className="has-text-weight-bold is-size-6">
                  WhatsApp Call
                </p>
                <p className={`is-size-7 ${getStatusColor()}`}>
                  {call.from} • {getStatusDisplay()}
                </p>
              </div>
            </div>
            <div className="is-flex is-align-items-center">
              <button
                className="button is-small is-ghost mr-2"
                onClick={() => setIsMinimized(false)}
              >
                <FaExpand size={14} />
              </button>
              <button
                className={`button is-danger is-rounded is-small ${isTerminating ? 'is-loading' : ''}`}
                onClick={onTerminate}
                disabled={isTerminating}
              >
                {!isTerminating && <FaPhoneSlash size={12} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal
      isOpen
      title={() => (
        <div className="is-flex is-justify-content-space-between is-align-items-center w-100">
          <h2 className="title is-5 mb-0">WhatsApp Call</h2>
          <button
            className="button is-small"
            onClick={() => setIsMinimized(true)}
          >
            <FaMinus size={14} />
          </button>
        </div>
      )}
    >
      <div className="has-text-centered p-4">
        <div className="is-flex is-justify-content-center mb-4">
          <div
            className="is-flex is-justify-content-center is-align-items-center has-background-success-light"
            style={{ width: "80px", height: "80px", borderRadius: "50%" }}
          >
            <FaPhoneAlt className="has-text-success" size={40} />
          </div>
        </div>

        <p className="is-size-4 mb-2">
          <strong>{call.direction === 'inbound' ? call.from : call.to}</strong>
        </p>
        
        <p className={`is-size-5 mb-4 ${getStatusColor()}`}>
          {getStatusDisplay()}
        </p>

        <div className="mb-4">
          <small className="has-text-grey">
            Connection: {connectionState}
          </small>
        </div>

        {/* Hidden audio element for remote stream */}
        <audio
          id="whatsapp-remote-audio"
          autoPlay
          style={{ display: 'none' }}
        />

        <button
          className={`button is-danger is-rounded ${isTerminating ? 'is-loading' : ''}`}
          onClick={onTerminate}
          disabled={isTerminating}
          style={{ width: '60px', height: '60px' }}
        >
          {!isTerminating && <FaPhoneSlash size={24} />}
        </button>
      </div>
    </Modal>
  );
};